import type { DietTypes, KnownAllergyId, LifestyleTags, MealType, Recipe, RecipeCategory } from '../types';
import { detectRecipeAllergens } from './allergies';
import { normalizeDietTypes } from './dietTypes';
import {
    buildAutoTaggedRecipeMetadata,
    getResolvedRecipeDietTypes,
    normalizeSuitableFor,
} from './recipeFilterEstimation';
import {
    estimateLifestyleTags,
    mergeEstimatedLifestyleTags,
    normalizeLifestyleTags,
} from './lifestyleTags';

const DEFAULT_PREP_TIME_MINUTES = 20;

export type RecipeFilterMetadataField =
    | 'dietTypes'
    | 'isKeto'
    | 'suitableFor'
    | 'lifestyleTags'
    | 'category'
    | 'prepTime'
    | 'allergens';

export interface RecipeFilterMetadataMigrationPatch {
    readonly updates: Readonly<Record<string, unknown>>;
    readonly deleteFields: readonly string[];
    readonly changedFields: readonly RecipeFilterMetadataField[];
}

function normalizePrepTime(value: unknown): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0
        ? Math.round(value)
        : DEFAULT_PREP_TIME_MINUTES;
}

function dietTypesEqual(left: DietTypes, right: DietTypes): boolean {
    return (Object.keys(left) as (keyof DietTypes)[]).every((key) => left[key] === right[key]);
}

function lifestyleTagsEqual(left: LifestyleTags, right: LifestyleTags): boolean {
    return left.isKidFriendly === right.isKidFriendly
        && left.isQuickPrep === right.isQuickPrep
        && left.isBudgetFriendly === right.isBudgetFriendly;
}

function mealTypesEqual(left: MealType[], right: MealType[]): boolean {
    if (left.length !== right.length) return false;
    const leftSorted = [...left].sort();
    const rightSorted = [...right].sort();
    return leftSorted.every((meal, index) => meal === rightSorted[index]);
}

function allergensEqual(left: KnownAllergyId[], right: KnownAllergyId[]): boolean {
    if (left.length !== right.length) return false;
    const leftSorted = [...left].sort();
    const rightSorted = [...right].sort();
    return leftSorted.every((allergen, index) => allergen === rightSorted[index]);
}

export function buildRecipeFilterMetadataPatch(
    recipe: Recipe,
    now = new Date().toISOString(),
): RecipeFilterMetadataMigrationPatch | null {
    if (recipe.isDeleted) return null;

    const updates: Record<string, unknown> = {};
    const deleteFields: string[] = [];
    const changedFields: RecipeFilterMetadataField[] = [];

    const autoTagged = buildAutoTaggedRecipeMetadata(recipe);
    const currentDietTypes = getResolvedRecipeDietTypes(recipe);
    const needsLegacyKetoRemoval = recipe.isKeto !== undefined;

    if (!recipe.dietTypes || !dietTypesEqual(currentDietTypes, autoTagged.dietTypes)) {
        updates.dietTypes = autoTagged.dietTypes;
        changedFields.push('dietTypes');
    }
    if (needsLegacyKetoRemoval) {
        deleteFields.push('isKeto');
        changedFields.push('isKeto');
    }

    const storedSuitableFor = normalizeSuitableFor(recipe.suitableFor);
    if (storedSuitableFor.length === 0 || !mealTypesEqual(storedSuitableFor, autoTagged.suitableFor)) {
        updates.suitableFor = autoTagged.suitableFor;
        changedFields.push('suitableFor');
    }

    const normalizedLifestyleTags = mergeEstimatedLifestyleTags(
        normalizeLifestyleTags(recipe.lifestyleTags),
        estimateLifestyleTags(recipe),
    );
    const currentLifestyleTags = normalizeLifestyleTags(recipe.lifestyleTags);
    if (!lifestyleTagsEqual(currentLifestyleTags, normalizedLifestyleTags)) {
        updates.lifestyleTags = normalizedLifestyleTags;
        changedFields.push('lifestyleTags');
    }

    if (recipe.category !== autoTagged.category) {
        updates.category = autoTagged.category;
        changedFields.push('category');
    }

    const normalizedPrepTime = normalizePrepTime(recipe.prepTime);
    if (recipe.prepTime !== normalizedPrepTime) {
        updates.prepTime = normalizedPrepTime;
        changedFields.push('prepTime');
    }

    const detectedAllergens = detectRecipeAllergens(recipe);
    const currentAllergens = Array.isArray(recipe.allergens)
        ? recipe.allergens
        : [];
    if (!allergensEqual(currentAllergens, detectedAllergens)) {
        updates.allergens = detectedAllergens;
        changedFields.push('allergens');
    }

    if (changedFields.length === 0) {
        return null;
    }

    updates.updatedAt = now;

    return {
        updates,
        deleteFields,
        changedFields,
    };
}

export function shouldMigrateRecipeFilterMetadata(recipe: Recipe): boolean {
    return buildRecipeFilterMetadataPatch(recipe) != null;
}

export function collectRecipeFilterMetadataCandidates(recipes: readonly Recipe[]): Recipe[] {
    return recipes.filter((recipe) => shouldMigrateRecipeFilterMetadata(recipe));
}

export interface RecipeFilterMetadataMigrationSummary {
    readonly scanned: number;
    readonly updated: number;
    readonly fieldCounts: Readonly<Record<RecipeFilterMetadataField, number>>;
}

export function summarizeRecipeFilterMetadataCandidates(recipes: readonly Recipe[]): RecipeFilterMetadataMigrationSummary {
    const fieldCounts: Record<RecipeFilterMetadataField, number> = {
        dietTypes: 0,
        isKeto: 0,
        suitableFor: 0,
        lifestyleTags: 0,
        category: 0,
        prepTime: 0,
        allergens: 0,
    };

    let updated = 0;
    for (const recipe of recipes) {
        const patch = buildRecipeFilterMetadataPatch(recipe);
        if (!patch) continue;
        updated += 1;
        for (const field of patch.changedFields) {
            fieldCounts[field] += 1;
        }
    }

    return {
        scanned: recipes.length,
        updated,
        fieldCounts,
    };
}
